import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { APP_CONFIG } from '../constants/app.constants';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  // TODO: move to environment
  private readonly baseUrl = APP_CONFIG.url;

  // Simple in-memory cache for currencies list
  private currenciesCache: any | null = null;

  getLocations(params: any): Observable<any> {
    return this.http.get(`${this.baseUrl}/auto_complete`, { params });
  }

  getLocationContent(
    page: number = 1,
    locationId: string | number,
    keyName: string = 'region_raw',
    page_size = 9,
    filters: any = null,
    curator_destination_id?: string | number | null,
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', page_size.toString())
      .set(keyName, locationId.toString());

    if (curator_destination_id != null && curator_destination_id !== '') {
      params = params.set('curator_destination_id', curator_destination_id.toString());
    }

    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value.toString());
        }
      });
    }

    return this.http.get(`${this.baseUrl}/v1/location_content`, { params });
  }

  getHotelSearch(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/v2/search_api`, body, { headers: { 'Content-Type': 'text/plain' } });
  }

  postSearchV2(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/v2/search_api`, body);
  }

  getPackage(packageBuilderId: string, pollCount: number = 1, limit: number = 100, bitParams: any = []): Observable<any> {
    const fd = new FormData();
    fd.append('package_id', packageBuilderId);
    fd.append('limit', String(limit));
    fd.append('poll_count', String(pollCount));
    fd.append('bit_params', JSON.stringify(bitParams));
    return this.http.post(`${this.baseUrl}/v2/get_package`, fd);
  }

  getHotelFilrers(payload: any): Observable<any> {
    const params = new HttpParams()
    .set('package_id', payload.package_id)
    .set('limit', String(payload.limit))
    .set('poll_count', String(payload.poll_count))
    .set('bit_params', JSON.stringify(payload.bit_params));
    return this.http.post(`${this.baseUrl}/v1/get_hotel_filter`, params, { headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' } });
  }

  getStatics(hotels: any, locationId?: any): Observable<any> {
    const params = new HttpParams()
    .set('hotels', locationId ? `${locationId}:${hotels.join(',')}` : hotels.join(',')); 
    return this.http.get(`${this.baseUrl}/get_static/hotel`, { params: params });
  }

  getNearbyAttractions(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' });
    return this.http.post(`${this.baseUrl}/hotel_nearby_attraction`, payload, { headers });
  }

  roomAndRates(payload: any): Observable<any> {
  
    return this.http.post(`${this.baseUrl}/partners_metrip/get/hotel/rooms_and_rates/v2`, payload);
  }

  getWebConfig(): Observable<any> {
    const payload = {
      data: {
        subdomain: APP_CONFIG.webconfigURL
      }
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/v1/get_webconfig`, payload, { 
      headers,
      withCredentials: true
    });
  }

  // Lead Service: put itinerary
  putItinerary(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    // MTNewUI posts JSON string { data: params }
    const body = { data };
    return this.http.post(`${this.baseUrl}/api/lead/lead_service/v1/put_itinerary/`, body, { headers });
  }

  // Clone Package V2
  clonePackageV2(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/v2/clone_package`, payload, { headers });
  }

  // Calculate Price V1
  calculatePrice(params: { lead_id: number | string; agency_id: number | string; partner: string; currency: string }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/v1/calculate_price`, params, { headers });
  }

  // Lead Service: check itinerary (fetch existing lead for package/user)
  checkLeadItinerary(params: any): Observable<any> {
    let httpParams = new HttpParams().set('package_id', params.package_id);
    if (params.user_ui_id) httpParams = httpParams.set('user_ui_id', params.user_ui_id);
    return this.http.get(`${this.baseUrl}/api/lead/lead_service/v1/check_itinerary/`, { params: httpParams });
  }

  // Auth: check current session/auth status
  checkAuth(): Observable<any> {
    return this.http.get(`${this.baseUrl}/check_auth/?utm_profile=0&broadcast=false`);
  }

  // save traveler information
  saveTravelerInformation(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/traveler_info/api/v1/traveller-details/save`, payload, { headers, withCredentials: true , observe: 'response'});
  }

  getTravelerQueries(request_id: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(`${this.baseUrl}/api/traveler_info/api/v2/traveller-questions?request_id=${request_id}`, { headers });
  }

  setSessionId(): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(`${this.baseUrl}/search/trips`, { headers });
  }

  // Traveler API Calls
  createTraveler(payload: any): Observable<any> {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    headers = headers.set('mt-client', 'App');
    return this.http.post(`${this.baseUrl}/api/auth/api/v1/user/create`, payload, { headers });
  }

  // Create User (Private Access)
  createUser(payload: { email: string; role: string; guest: boolean }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/auth/api/v1/user/create`, payload, { headers, withCredentials: true });
  }

  submitContactUs(payload: {
    name: string;
    email: string;
    message: string;
    mobile?: string;
    country?: string;
  }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/auth/api/v1/user/contact-us`, payload, {
      headers,
      withCredentials: true,
    });
  }

  // User Access (Coming Soon)
  userAccess(payload: { email: string }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/auth/api/v1/user/access`, payload, { headers, withCredentials: true });
  }

  getTravelerDetails(request_id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/traveler_info/api/v1/traveller-details?request_id=${request_id}`);
  }

  //BNPL
  getBNPLDetails(search_id: string, lead_id: string, agency_id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/bnpl/v1/cache/${search_id}/lead/${lead_id}/agency/${agency_id}/plan`);
  }

  updateBNPLDetails(payload): Observable<any> {
    return this.http.put(`${this.baseUrl}/api/bnpl/v1/plan/cache`, payload );
  }

  getUserDetails(extend_session: boolean = false): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/auth/api/v1/user/details?extend_session=${extend_session}`);
  }

  getPaymentMethods(partner_id: string = APP_CONFIG.partner): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/payment/get_payment_methods/?partner_id=${partner_id}`);
  }

  getUserCredit(payload: any): Observable<any> {
    const { currency, partner, payment_method_id, lead_id, agency_user_id, partner_id } = payload;
    return this.http.get(`${this.baseUrl}/api/payment/get_payment_prereqs/?currency=${currency}&partner=${partner}&payment_method_id=${payment_method_id}&lead_id=${lead_id}&agency_user_id=${agency_user_id}&partner_id=${partner_id}`);
  }

  getPaymentPrereqs(payload: any): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/payment/get_payment_prereqs/?currency=${payload.currency}&partner=${payload.partner}&payment_method_id=${payload.payment_method_id}&lead_id=${payload.lead_id}&agency_user_id=${payload.agency_user_id}&partner_id=${payload.partner_id}`);
  }

  makePayment(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/payment/make_payment/`, payload, { 
      headers,
      withCredentials: true 
    });
  }

  savePayment(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/payment/save_payment/`, payload, { 
      headers,
      withCredentials: true
    });
  }

  repriceBooking(lead_id: number | string): Observable<any> {
    const payload = {
      lead_id: lead_id
    }
    return this.http.post(`${this.baseUrl}/api/booking/reprice/`, payload);
  }

  // BOOKING APIs
  setPlanCache(payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/api/bnpl/v1/plan/cache`, payload);
  }

  book(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/booking/book/`, payload);
  }

  generateVoucher(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/booking/generate-voucher/`, payload);
  }

  // Booking Details
  // api/booking/get-booking-details/
  getBookingDetails(booking_id: string): Observable<any> {
    const payload = {
      booking_id: booking_id
    }
    return this.http.post(`${this.baseUrl}/api/booking/get-booking-details/`, payload);
  }
  // BOOKING_LIST API
  getBookingList(filters?: {
    booking_status?: string;
    payment_status?: string;
    supplier_ref?: string;
    booking_type?: string;
    search_origin?: string;
    search_lead_traveller?: string;
    search_booking_id?: string;
    lead_id?: string;
    is_hold?: string;
    created_at__gte?: string;
    created_at__lte?: string;
    travel_date__gte?: string;
    travel_date__lte?: string;
    travel_date?: string;
    plan_expiration_date__gte?: string;
    plan_expiration_date__lte?: string;
    limit?: number;
    offset?: number;
  }): Observable<any> {
    let params = new HttpParams();

    // Filter parameters
    if (filters?.booking_status) {
      params = params.set('booking_status', filters.booking_status);
    }
    if (filters?.payment_status) {
      params = params.set('payment_status', filters.payment_status);
    }
    if (filters?.supplier_ref) {
      params = params.set('supplier_ref', filters.supplier_ref);
    }
    if (filters?.booking_type) {
      params = params.set('booking_type', filters.booking_type);
    }
    if (filters?.search_origin) {
      params = params.set('search_origin', filters.search_origin);
    }
    if (filters?.search_lead_traveller) {
      params = params.set('search_lead_traveller', filters.search_lead_traveller);
    }
    if (filters?.search_booking_id) {
      params = params.set('search_booking_id', filters.search_booking_id);
    }
    if (filters?.lead_id) {
      params = params.set('lead_id', filters.lead_id);
    }
    if (filters?.is_hold) {
      params = params.set('is_hold', filters.is_hold);
    }

    // Date range parameters
    if (filters?.created_at__gte) {
      params = params.set('created_at__gte', filters.created_at__gte);
    }
    if (filters?.created_at__lte) {
      params = params.set('created_at__lte', filters.created_at__lte);
    }
    if (filters?.travel_date__gte) {
      params = params.set('travel_date__gte', filters.travel_date__gte);
    }
    if (filters?.travel_date__lte) {
      params = params.set('travel_date__lte', filters.travel_date__lte);
    }
    if (filters?.travel_date) {
      params = params.set('travel_date', filters.travel_date);
    }
    if (filters?.plan_expiration_date__gte) {
      params = params.set('plan_expiration_date__gte', filters.plan_expiration_date__gte);
    }
    if (filters?.plan_expiration_date__lte) {
      params = params.set('plan_expiration_date__lte', filters.plan_expiration_date__lte);
    }

    // Pagination parameters
    if (filters?.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }
    if (filters?.offset !== undefined) {
      params = params.set('offset', filters.offset.toString());
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    });

    return this.http.get(`${this.baseUrl}/api/booking/booking-list/`, {
      params,
      headers
    });
  }

  // CANCELLATION APIs
  cancelPackage(
    booking_id: string,
    package_booking_id: number | number[],
    itinerary_booking_id: number | number[],
    isCompleteCancellation: boolean = false,
    reason: string | null = null,
    isComplete: boolean = false,
    extra_info: any = null,
    pathfndr_id: string = '',
    itinerary_id: string = ''
  ): Observable<any> {
    const payload = this.constructCancelParams(
      booking_id,
      package_booking_id,
      itinerary_booking_id,
      isCompleteCancellation,
      reason,
      isComplete,
      extra_info,
      pathfndr_id,
      itinerary_id
    );

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/booking/cancel/`, payload, { headers });
  }

  private constructCancelParams(
    booking_id: string,
    package_booking_id: number | number[],
    itinerary_booking_id: number | number[],
    isCompleteCancellation: boolean,
    reason: string | null,
    isComplete: boolean,
    extraInfoData: any,
    pathfndr_id: string,
    itinerary_id: string
  ): any {
    // Ensure arrays
    const packageIds = Array.isArray(package_booking_id) ? package_booking_id : [package_booking_id];
    const itineraryIds = Array.isArray(itinerary_booking_id) ? itinerary_booking_id : [itinerary_booking_id];

    return {
      booking_id: booking_id,
      // complete: isComplete,
      packages: packageIds.map((pkgId, index) => ({
        package_booking_id: pkgId,
        itinerary_to_cancel: itineraryIds,
        // cancellation_reason_id: reason,
        complete_cancellation: isCompleteCancellation,
        // extra_info: extraInfoData && extraInfoData.length > 0 ? extraInfoData[0] : {},
        // extra_info: { itinerary_id : { remarks : reason } },
        extra_info: { [itinerary_id?.toString()]: { remarks : reason } },
        pathfndr_id: pathfndr_id
      }))
    };
  }

  combinedVoucherUrl(booking_id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/booking/combined-voucher-url/?booking_id=${booking_id}`);
  }

  // Check Hotel Availability
  checkHotelAvailability(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/v1/check_hotel_availability`, payload, { headers });
  }

  // save additional information
  saveAdditionalInformation(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/traveler_info/api/v1/answers/save`, payload, { headers });
  }

  // Wishlist APIs
  createWishlist(payload: any): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/b2b/wishlist/v1/create/`, payload, { 
      headers,
      withCredentials: true 
    });
  }

  addItemToWishlist(wishlistId: string, payload: { item_id: string; item_type: string }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}/api/b2b/wishlist/v1/${wishlistId}/add/`, payload, { 
      headers,
      withCredentials: true 
    });
  }

  listWishlists(): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(`${this.baseUrl}/api/b2b/wishlist/v1/list/`, { 
      headers,
      withCredentials: true 
    });
  }

  listWishlistItems(wishlistId: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(`${this.baseUrl}/api/b2b/wishlist/v1/${wishlistId}/items/`, { 
      headers,
      withCredentials: true 
    });
  }

  getWishlist(wishlistId: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(`${this.baseUrl}/api/b2b/wishlist/v1/${wishlistId}/`, { 
      headers,
      withCredentials: true 
    });
  }

  getCurrencies(): Observable<any> {
    if (this.currenciesCache) {
      // Return cached value as observable
      return of(this.currenciesCache);
    }

    return this.http.get(`${this.baseUrl}/get_currency`).pipe(
      tap((data: any) => {
        this.currenciesCache = data;
      }),
    );
  }

  ipGeoLookup(ipAddress: string): Observable<IpGeoLookupResponse> {
    const params = new HttpParams().set('ipAddress', ipAddress);
    return this.http.get<IpGeoLookupResponse>(`${this.baseUrl}/v1/search/ip_geo_lookup`, { params });
  }
}

export interface IpGeoLookupResponse {
  ip: string;
  location: {
    country: string;
    region: string;
    city: string;
    lat: number;
    lng: number;
    postalCode: string;
    timezone: string;
    geonameId: number;
  };
  as?: {
    asn: number;
    name: string;
    route: string;
    domain: string;
    type: string;
  };
  isp?: string;
  proxy?: {
    proxy: boolean;
    vpn: boolean;
    tor: boolean;
  };
}


